import { EDocumentType } from '@precheckin/models/enums/document-type.enum';
import { cloneDeep } from 'lodash';
import { DateTime } from 'luxon';

import { VisitDocumentMetaData } from './visit-document-metadata.model';
import { VisitDocumentPage } from './visit-document-page.model';
import { EDocumentStatus } from '../enums/document-status.enum';
import { EVisitDocumentMetaData } from '../enums/visit-document-metadata.enum';

export interface IVisitDocument {
  id?: string;
  visitId?: string;
  type?: EDocumentType;
  status?: EDocumentStatus;
  pages?: VisitDocumentPage[];
  expirationDate?: Date;
  metaData?: object;
  statusComment?: string;
  rejectionReasonCode?: string;
  rejectionReasonDescription?: string;
}

export class VisitDocument implements IVisitDocument {
  id: string;
  visitId: string;
  type: EDocumentType;
  status: EDocumentStatus;
  pages: VisitDocumentPage[];
  metaData: Map<string, VisitDocumentMetaData> | object;
  statusComment: string;
  rejectionReasonCode: string;
  rejectionReasonDescription: string;

  constructor(clone: IVisitDocument = null) {
    this.id = '';
    this.visitId = '';
    this.type = null;
    this.status = EDocumentStatus.PENDING_VALIDATION;
    this.pages = new Array<VisitDocumentPage>();
    this.metaData = new Map<string, VisitDocumentMetaData>();
    this.statusComment = '';
    this.rejectionReasonCode = '';
    this.rejectionReasonDescription = '';

    if (clone) {
      this.id = cloneDeep(clone.id);
      this.visitId = cloneDeep(clone.visitId);
      this.type = cloneDeep(clone.type);
      this.status = cloneDeep(clone.status);
      this.pages = cloneDeep(clone.pages);
      this.statusComment = cloneDeep(clone.statusComment);
      this.rejectionReasonCode = cloneDeep(clone.rejectionReasonCode);
      this.rejectionReasonDescription = cloneDeep(clone.rejectionReasonDescription);

      const cloneMetaData = cloneDeep(clone.metaData);

      if (cloneMetaData) {
        const keys = Object.keys(cloneMetaData);
        if (keys.length) {
          for (const [key, metaData] of Object.entries(cloneMetaData)) {
            (this.metaData as Map<string, VisitDocumentMetaData>).set(
              key,
              new VisitDocumentMetaData(metaData)
            );
          }
        } else {
          this.metaData = Object.values(cloneMetaData).some(
            (v) => v !== null && typeof v !== 'undefined'
          )
            ? new Map<string, VisitDocumentMetaData>(
              cloneMetaData as Map<string, VisitDocumentMetaData>
            )
            : cloneMetaData;
        }
      }
    }
  }

  getMetaDataValue(
    key: EVisitDocumentMetaData
  ): string | number | boolean | DateTime<boolean> {
    if (!this.metaData) {
      return null;
    }
    if (!(this.metaData as Map<string, VisitDocumentMetaData>).has(key)) {
      return null;
    }
    return (this.metaData as Map<string, VisitDocumentMetaData>).get(key).value;
  }

  setMetaDataValue(key: EVisitDocumentMetaData, value: any): void {
    switch (key) {
      case EVisitDocumentMetaData.EXPIRATION_DATE:
      case EVisitDocumentMetaData.EXAMINATION_DATE:
        (this.metaData as Map<string, VisitDocumentMetaData>).set(
          key,
          new VisitDocumentMetaData({
            value: value ? (value as DateTime).toISODate() : value,
            type: 'DateTime'
          })
        );
        break;
      default:
        (this.metaData as Map<string, VisitDocumentMetaData>).set(
          key,
          new VisitDocumentMetaData({
            value: value ? value : '',
            type: 'String'
          })
        );
        break;
    }
  }

  removeMetaData(keyToRemove: EVisitDocumentMetaData): void {
    if (!this.metaData) {
      return;
    }

    const metaDataMap = this.metaData as Map<string, VisitDocumentMetaData>;

    if (metaDataMap.has(keyToRemove)) {
      metaDataMap.delete(keyToRemove);
    }
  }

  hasMetaData(
    key: EVisitDocumentMetaData
  ): boolean {
    if (!this.metaData) {
      return false;
    }

    return (this.metaData as Map<string, VisitDocumentMetaData>).has(key);
  }
}
